import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useCalEmbed, openCalModal } from "@/hooks/useCalEmbed";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ComparisonTable } from "./components/ComparisonTable";
import { TableOfContents } from "./components/TableOfContents";
import { Callout } from "./components/Callout";
import { StickyMobileCTA } from "./components/StickyMobileCTA";
import { FreeTimer } from "./components/FreeTimer";
import { COMPETITOR_PRICES } from "./config/competitorPrices";
import {
  Timer, Clock, CalendarDays, Zap, BarChart3, Keyboard, Star, Copy,
  Check, ChevronDown, ChevronUp, ArrowRight, Target, FileText,
  Users, Layers, Play, X, Video, Download, Scale, Calculator, FileSpreadsheet
} from "lucide-react";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        style={{ display: "flex" }} className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s/g, '-')}`}
      >
        <span className="font-medium text-gray-900 pr-4">{question}</span>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>
      <p className={`text-gray-600 pb-4 text-sm leading-relaxed ${open ? "block" : "hidden"}`}>{answer}</p>
    </div>
  );
}

export default function ZeiterfassungFreelancer() {
  const navigate = useNavigate();
  useCalEmbed();

  useSEO({
    title: "Zeiterfassung Freelancer kostenlos – Timer online | ivoliq",
    description: "Kostenlose Zeiterfassung für Freelancer: Timer im Browser starten, Stunden pro Projekt erfassen, CSV exportieren – mit ivoliq per Klick abrechnen.",
    canonical: "https://ivoliq.io/zeiterfassung-freelancer",
  });

  const features = [
    {
      icon: Play,
      title: "One-Click Timer",
      desc: "Starte die Zeiterfassung mit einem Klick. Ordne sie direkt einem Projekt und Kunden zu. Funktioniert auch im Hintergrund.",
    },
    {
      icon: CalendarDays,
      title: "Kalenderansicht",
      desc: "Wochenkalender im Google-Calendar-Stil. Zeitblöcke per Drag & Drop verschieben und Einträge direkt anlegen.",
    },
    {
      icon: Keyboard,
      title: "Keyboard Shortcuts",
      desc: "N für neuen Eintrag, T für Timer, Pfeiltasten für Wochennavigation. Für Power-User, die keine Maus brauchen.",
    },
    {
      icon: Copy,
      title: "Einträge duplizieren",
      desc: "Wiederkehrende Tätigkeiten mit einem Klick kopieren. Ganze Tage oder Wochen duplizieren.",
    },
    {
      icon: Star,
      title: "Favoriten & Vorlagen",
      desc: "Speichere häufige Projekt-Aufgaben-Kombis als Favoriten. Ein Klick statt 5 Felder ausfüllen.",
    },
    {
      icon: Target,
      title: "Wochenziel",
      desc: "Setze dir ein Stundenziel pro Woche. Sieh auf einen Blick, wie weit du bist. Motivation inklusive.",
    },
    {
      icon: BarChart3,
      title: "Projekt-Auswertung",
      desc: "Erfasste Stunden pro Projekt, Kunde und Zeitraum. Sieh sofort, welche Projekte profitabel sind.",
    },
    {
      icon: FileText,
      title: "Rechnung mit einem Klick",
      desc: "Wähle Zeiteinträge aus und erstelle daraus eine rechtssichere Rechnung. Stundensatz wird automatisch übernommen.",
    },
  ];

  const comparisonData = [
    { label: "Integrierter Timer", values: { ivoliq: true, toggl: true, sevdesk: { text: `Zusatzmodul ${COMPETITOR_PRICES.sevdesk.addons[0].price}`, ok: false }, lexoffice: { text: "nur über Partner-Apps", ok: false } } },
    { label: "Kalenderansicht (Drag & Drop)", values: { ivoliq: true, toggl: true, sevdesk: false, lexoffice: false } },
    { label: "Aus Zeiteinträgen direkt Rechnung erstellen", values: { ivoliq: { text: "ein Klick", ok: true }, toggl: false, sevdesk: { text: "mit Zusatzmodul", ok: true }, lexoffice: { text: "manuell übertragen", ok: false } } },
    { label: "Keyboard Shortcuts", values: { ivoliq: true, toggl: true, sevdesk: false, lexoffice: false } },
    { label: "Favoriten / Vorlagen", values: { ivoliq: true, toggl: false, sevdesk: false, lexoffice: false } },
    { label: "Steuer-Rücklage live", note: "ESt/SVS/AHV", values: { ivoliq: { text: "DE, AT & CH", ok: true }, toggl: false, sevdesk: { text: "nur DE", ok: true }, lexoffice: { text: "nur DE", ok: true } } },
    { label: "Swiss QR-Bill", values: { ivoliq: true, toggl: false, sevdesk: false, lexoffice: false } },
    { label: "Kostenloser Plan", values: { ivoliq: { text: "Free für immer", ok: true }, toggl: { text: "bis 5 Nutzer", ok: true }, sevdesk: { text: "max. 3 Rechnungen/Monat" }, lexoffice: false } },
    { label: "Preis mit Zeiterfassung + Rechnung / Monat", note: `Listenpreise netto, Stand ${COMPETITOR_PRICES.sevdesk.asOf}`, values: { ivoliq: "0 € (Pro 19 €)", toggl: "Timer only – Rechnung separat", sevdesk: `${COMPETITOR_PRICES.sevdesk.plans[0].price} + ${COMPETITOR_PRICES.sevdesk.addons[0].price}`, lexoffice: `${COMPETITOR_PRICES.lexoffice.price} (L) + Zeit-App` } },
  ];
  const painPoints = [
    { label: "Stunden aus Toggl/Excel exportieren", values: { tools: { text: "manuell, jede Rechnung", ok: false }, ivoliq: { text: "Zeiteinträge liegen direkt neben der Rechnung", ok: true } } },
    { label: "Positionen in die Rechnung tippen", values: { tools: { text: "abtippen, Fehlerquelle", ok: false }, ivoliq: { text: "Einträge auswählen → Positionen entstehen automatisch", ok: true } } },
    { label: "Stundensätze pro Kunde nachschlagen", values: { tools: { text: "Excel-Liste", ok: false }, ivoliq: { text: "Stundensatz ist am Projekt hinterlegt", ok: true } } },
    { label: "Leistungsnachweis erstellen", values: { tools: { text: "separat in Excel bauen", ok: false }, ivoliq: { text: "Stundenaufstellung hängt automatisch an", ok: true } } },
    { label: "Doppelt abgerechnete Stunden", values: { tools: { text: "passiert", ok: false }, ivoliq: { text: "abgerechnete Einträge werden markiert", ok: true } } },
  ];
  const tocItems = [
    { id: "features", label: "Funktionen" },
    { id: "vergleich", label: "Tool-Vergleich" },
    { id: "rechnung", label: "Zeit → Rechnung" },
    { id: "vorlage", label: "Vorlage" },
    { id: "guide", label: "Guide" },
    { id: "faq", label: "FAQ" },
  ];

  const useCases = [
    { role: "Entwickler & IT-Berater", desc: "Tracke Coding-Sessions per Timer. Erstelle aus Sprints direkt Rechnungen. Stundenbasiert oder Pauschal." },
    { role: "Designer & Kreative", desc: "Erfasse Entwurfs- und Korrekturschleifen separat. Sieh, wie viel Zeit pro Revision draufgeht." },
    { role: "Berater & Coaches", desc: "Timer für Beratungstermine. Fahrtzeiten separat erfassen. Direkt fakturieren." },
    { role: "Texter & Redakteure", desc: "Wortpreise mit Zeitaufwand vergleichen. Sieh deinen effektiven Stundensatz pro Auftrag." },
  ];

  const faqItems = [
    {
      q: "Wie funktioniert die Zeiterfassung mit ivoliq?",
      a: "Starte den Timer mit einem Klick, ordne die Zeit einem Projekt zu und stoppe wenn du fertig bist. Alternativ kannst du Zeiten auch manuell nachtragen. Alle Einträge erscheinen in einer übersichtlichen Liste und Kalenderansicht.",
    },
    {
      q: "Kann ich aus Zeiteinträgen direkt Rechnungen erstellen?",
      a: "Ja – das ist der Kern von ivoliq. Wähle die gewünschten Zeiteinträge aus, klicke auf 'Rechnung erstellen' und ivoliq generiert automatisch eine rechtssichere Rechnung mit allen Positionen, Stundensätzen und Steuern. Mit einem Klick.",
    },
    {
      q: "Ist die Zeiterfassung kostenlos?",
      a: "Ja, die Zeiterfassung mit Timer, manuellen Einträgen und Kalenderansicht ist im kostenlosen Free-Plan enthalten. Du kannst ivoliq dauerhaft kostenlos nutzen – ohne Kreditkarte, ohne Testphase-Limit.",
    },
    {
      q: "Gibt es eine Kalenderansicht?",
      a: "Ja, ivoliq bietet eine Wochenkalender-Ansicht im Google-Calendar-Stil. Du kannst Zeitblöcke per Drag & Drop verschieben, in der Größe ändern und direkt im Kalender neue Einträge anlegen.",
    },
    {
      q: "Funktioniert ivoliq auch für die Schweiz und Österreich?",
      a: "Ja. ivoliq ist speziell für den DACH-Raum gebaut. Zeiterfassung, Rechnungserstellung und Steuerberechnung funktionieren für Deutschland, Österreich und die Schweiz – inklusive Swiss QR-Bill für CH-Rechnungen.",
    },
    {
      q: "Was unterscheidet ivoliq von Toggl oder Clockify?",
      a: "Toggl und Clockify sind reine Zeiterfassungs-Tools. ivoliq verbindet Zeiterfassung mit Rechnungserstellung, Kundenverwaltung und Steuerberechnung in einem nahtlosen Workflow. Du brauchst keine 3 verschiedenen Tools mehr.",
    },
    {
      q: "Kann ich wiederkehrende Einträge duplizieren?",
      a: "Ja. Du kannst einzelne Einträge, ganze Tage oder ganze Wochen duplizieren. Zusätzlich gibt es Favoriten/Vorlagen für häufige Projekt-Aufgaben-Kombinationen. Ein Klick statt 5 Felder ausfüllen.",
    },
    {
      q: "Gibt es Keyboard Shortcuts?",
      a: "Ja. N für neuen Eintrag, T für Timer starten/stoppen, Pfeiltasten für Wochennavigation. ivoliq ist für Power-User gebaut, die schnell arbeiten wollen.",
    },
    {
      q: "Gibt es eine kostenlose Stundenzettel-Vorlage zum Download?",
      a: "Ja. Auf dieser Seite findest du eine kostenlose Stundenzettel-Vorlage als PDF (zum Ausdrucken) und als Excel-Datei (mit automatischer Summenberechnung). Kein E-Mail-Eintrag nötig – einfach herunterladen und loslegen.",
    },
    {
      q: "Bin ich als Freelancer gesetzlich zur Zeiterfassung verpflichtet?",
      a: "Für dich selbst in der Regel nicht: Arbeitszeitgesetze (ArbZG in Deutschland, AZG in Österreich, ArG in der Schweiz) gelten für Arbeitnehmer. Sobald du aber Mitarbeiter beschäftigst, musst du deren Zeiten erfassen. Und für Leistungsnachweise, Honorarstreitigkeiten und deine Kalkulation ist die eigene Zeiterfassung praktisch unverzichtbar.",
    },
    {
      q: "Wie finde ich den richtigen Stundensatz für meine erfassten Stunden?",
      a: "Rechne rückwärts: Wunsch-Einkommen plus Sozialabgaben, Steuern und Betriebskosten, geteilt durch deine realistisch fakturierbaren Stunden (meist nur 60–75 % der Arbeitszeit). Unser kostenloser Stundensatz-Rechner macht das in 60 Sekunden – für Deutschland, Österreich und die Schweiz.",
    },
    {
      q: "Was ist der Unterschied zwischen abrechenbaren und nicht abrechenbaren Stunden?",
      a: "Abrechenbar sind Stunden, die du einem Kunden in Rechnung stellst. Nicht abrechenbar sind Akquise, Buchhaltung, E-Mails und Weiterbildung. Erfasse beides getrennt – nur so kennst du deinen effektiven Stundensatz und erkennst, welche Kunden sich wirklich lohnen.",
    },
    {
      q: "Wie lange muss ich meine Zeitaufzeichnungen aufbewahren?",
      a: "Zeitaufzeichnungen, die Grundlage deiner Rechnungen sind, gelten als Buchhaltungsunterlagen: In Deutschland 10 Jahre (§ 147 AO), in Österreich 7 Jahre (§ 132 BAO), in der Schweiz 10 Jahre (Art. 958f OR). Digitale Aufbewahrung ist überall zulässig – in ivoliq bleiben deine Einträge dauerhaft mit den zugehörigen Rechnungen verknüpft.",
    },
    {
      q: "Kann ich Zeiten auch nachträglich erfassen oder korrigieren?",
      a: "Ja. Vergessene Einträge kannst du in ivoliq jederzeit manuell nachtragen und falsche Zuordnungen korrigieren – ideal im wöchentlichen Review. Wichtig für die Beweiskraft: möglichst zeitnah erfassen. Ein am selben Tag erstellter Eintrag wiegt vor Gericht und beim Finanzamt deutlich mehr als eine Wochen später rekonstruierte Schätzung.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ivoliq Zeiterfassung",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://ivoliq.io/zeiterfassung-freelancer",
    description: "Zeiterfassung für Freelancer mit integrierter Rechnungserstellung. Timer, Kalenderansicht, Keyboard Shortcuts – DACH-konform.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      description: "Kostenloser Plan für immer"
    },
    featureList: [
      "One-Click Timer mit Projektzuordnung",
      "Google-Calendar-Stil Wochenansicht",
      "Drag & Drop Zeitblöcke",
      "Rechnung erstellen mit einem Klick",
      "Keyboard Shortcuts",
      "Favoriten & Vorlagen",
      "DACH-Steuerberechnung",
      "Swiss QR-Bill Unterstützung"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-gray-900" data-testid="header-logo">ivoliq</Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Anmelden</Link>
            <Button size="sm" onClick={() => navigate("/signup")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="header-cta">
              Kostenlos starten
            </Button>
          </div>
        </div>
      </header>

      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }} />

      {/* ==================== HERO + KOSTENLOSER TIMER ==================== */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-emerald-50 to-white" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Timer className="h-4 w-4" /> Kostenlose Zeiterfassung für Freelancer
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Zeiterfassung online –<br />
              <span className="text-emerald-600">kostenlos, sofort, ohne Anmeldung.</span>
            </h1>
            <p className="text-lg text-gray-600 mt-6 max-w-2xl leading-relaxed">
              Starte den Timer direkt hier im Browser und erfasse deine Arbeitszeit pro Projekt. Mit einem kostenlosen ivoliq-Konto
              werden aus den Stunden später mit einem Klick Rechnungen – inklusive Leistungsnachweis und Steuer-Rücklage.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button
                size="lg"
                onClick={() => navigate("/signup")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                data-testid="hero-cta-register"
              >
                Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={openCalModal}
                data-testid="hero-cta-demo"
              >
                <Video className="h-5 w-5 mr-2" /> Kostenlose Demo buchen
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Keine Kreditkarte nötig &middot; Free Plan für immer &middot; DE/AT/CH
            </p>
          </div>
          <FreeTimer />
        </div>
      </section>

      <TableOfContents items={tocItems} top="top-16" />

      {/* ==================== PROBLEM / COST ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Jede ungetrackte Stunde kostet dich bares Geld
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                <X className="h-5 w-5" /> Ohne Zeiterfassung
              </h3>
              <ul className="space-y-3 text-sm text-red-700">
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> Vergisst du 30 Min/Tag = <strong>10h/Monat unbezahlt</strong></li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> Bei 80€/Std = <strong>800€ Umsatzverlust pro Monat</strong></li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> Kein Überblick, welche Projekte profitabel sind</li>
                <li className="flex items-start gap-2"><X className="h-4 w-4 mt-0.5 flex-shrink-0" /> Stundenlange Rechnungserstellung am Monatsende</li>
              </ul>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Check className="h-5 w-5" /> Mit ivoliq
              </h3>
              <ul className="space-y-3 text-sm text-emerald-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /> Jede Minute wird erfasst – <strong>kein Geld verschenkt</strong></li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /> Rechnung in 30 Sekunden aus Zeiteinträgen</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /> Profitabilität pro Projekt auf einen Blick</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" /> <strong>Spare 10 Stunden pro Monat</strong> an Verwaltung</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== WORKFLOW ==================== */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            Von der ersten Stunde zur fertigen Rechnung
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto">
            Der End-to-End Workflow, den kein anderes Tool so bietet.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Zeit erfassen", desc: "Timer starten oder Stunden manuell eintragen. Jeder Eintrag wird automatisch dem Projekt zugeordnet.", icon: Timer },
              { step: "2", title: "Überblick behalten", desc: "Kalenderansicht, Wochenziel, Projekt-Auswertung. Sieh sofort, wie profitabel deine Kunden sind.", icon: BarChart3 },
              { step: "3", title: "Rechnung erstellen", desc: "Wähle Zeiteinträge aus → Rechnung generieren → Versenden. Mit einem Klick. Rechtssicher nach DACH-Recht.", icon: FileText },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto text-xl font-bold mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section id="features" className="py-16 bg-white scroll-mt-28" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Gebaut für Freelancer, die schnell arbeiten
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <Card key={f.title} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                    <f.icon className="h-5 w-5 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-600">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== COMPARISON ==================== */}
      <section id="vergleich" className="py-16 bg-gray-50 scroll-mt-28" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">
            ivoliq vs. Toggl vs. sevDesk vs. lexoffice
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-xl mx-auto">
            Die einzige Zeiterfassung, die direkt Rechnungen erstellt – für den DACH-Raum.
          </p>
          <ComparisonTable
            caption="Zeiterfassungs-Tools für Freelancer im Vergleich"
            columns={[{ key: "ivoliq", label: "ivoliq", highlight: true }, { key: "toggl", label: "Toggl" }, { key: "sevdesk", label: "sevDesk" }, { key: "lexoffice", label: "lexoffice" }]}
            rows={comparisonData}
            footnote={`Quellen: ${COMPETITOR_PRICES.sevdesk.source}, ${COMPETITOR_PRICES.lexoffice.source}. Funktionsangaben nach öffentlicher Produktbeschreibung, Stand ${COMPETITOR_PRICES.lexoffice.asOf}.`}
          />
          <div className="mt-6 text-sm text-gray-600">
            Detailvergleiche: <Link to="/lexoffice-alternative" className="text-emerald-700 underline underline-offset-4">lexoffice Alternative</Link> · <Link to="/sevdesk-alternative" className="text-emerald-700 underline underline-offset-4">sevDesk Alternative</Link> · <Link to="/pricing" className="text-emerald-700 underline underline-offset-4">ivoliq-Preise</Link>
          </div>
        </div>
      </section>

      {/* ==================== ZEIT → RECHNUNG ==================== */}
      <section id="rechnung" className="py-16 bg-white scroll-mt-28" data-testid="rechnung-section">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">Zeiterfassung und Rechnungsstellung in einem Tool</h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            Der teuerste Schritt ist nicht das Tracken, sondern das Übertragen: Stunden exportieren, in die Rechnung tippen, Leistungsnachweis basteln. So sieht der Unterschied aus:
          </p>
          <ComparisonTable
            testId="pain-fix-table"
            caption="Getrennte Tools versus integrierte Zeiterfassung mit Rechnungsstellung"
            columns={[{ key: "tools", label: "Getrennte Tools" }, { key: "ivoliq", label: "ivoliq", highlight: true }]}
            rows={painPoints}
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Callout variant="tip" title="Rechne wöchentlich oder 14-tägig ab">Je kürzer der Abrechnungszyklus, desto besser dein Cashflow. Monatsrechnungen mit 30 Tagen Zahlungsziel bedeuten bis zu 60 Tage Wartezeit auf dein Geld.</Callout>
            <Callout variant="tip" title="Beschreibe Tätigkeiten schon beim Erfassen">„Landingpage: Hero-Sektion umgesetzt“ statt „Arbeit am Projekt“ – dann ist die Leistungsbeschreibung auf der Rechnung automatisch aussagekräftig.</Callout>
            <Callout variant="tip" title="Gruppiere nach Tätigkeit, nicht nach Tag">Sammelpositionen („Entwicklung: 12,5 h“) statt 30 Einzelpositionen – die detaillierte Stundenaufstellung geht als Anhang mit.</Callout>
            <Callout variant="info" title="Abrechenbar vs. nicht abrechenbar">Kläre im Angebot, ob Meetings und Fahrtzeiten fakturierbar sind. Erfasse trotzdem alles – nur so kennst du deinen echten Stundensatz. Zum <Link to="/stundensatz-rechner" className="underline underline-offset-4">Stundensatz-Rechner</Link>.</Callout>
          </div>
          <p className="mt-8 text-sm text-gray-600 text-center">
            Was von der Rechnung übrig bleibt, zeigt dir der <Link to="/steuerruecklagen-freelancer-oesterreich" className="text-emerald-700 underline underline-offset-4">Steuerrücklagen-Rechner für Österreich</Link> (auch für <Link to="/steuerruecklagen-freelancer-deutschland" className="text-emerald-700 underline underline-offset-4">Deutschland</Link> und die <Link to="/steuerruecklagen-freelancer-schweiz" className="text-emerald-700 underline underline-offset-4">Schweiz</Link>).
          </p>
        </div>
      </section>

      {/* ==================== USE CASES ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-12">
            Für jeden Freelancer-Typ optimiert
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {useCases.map((uc) => (
              <div key={uc.role} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="h-5 w-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">{uc.role}</h3>
                </div>
                <p className="text-sm text-gray-600">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== STUNDENZETTEL-VORLAGE DOWNLOAD ==================== */}
      <section id="vorlage" className="py-16 bg-gray-50 scroll-mt-28" data-testid="template-download-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Download className="h-4 w-4" /> Kostenloser Download
            </div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3">
              Stundenzettel-Vorlage: PDF & Excel kostenlos herunterladen
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm leading-relaxed">
              Du willst erstmal ohne Tool starten? Kein Problem. Unsere Stundenzettel-Vorlage enthält
              alles, was ein sauberer Wochennachweis braucht: Datum, Projekt/Kunde, Tätigkeit,
              Start- und Endzeit, Pausen und Gesamtstunden – inklusive Unterschriftsfeld für den Auftraggeber.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <a
              href="/downloads/stundenzettel-vorlage.pdf"
              download
              className="bg-white rounded-xl border shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col items-center text-center"
              data-testid="download-template-pdf"
            >
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center mb-3">
                <FileText className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Stundenzettel als PDF</h3>
              <p className="text-sm text-gray-600 mb-4">Zum Ausdrucken & handschriftlichen Ausfüllen. Ideal für unterwegs.</p>
              <span className="inline-flex items-center gap-2 text-emerald-600 font-medium text-sm">
                <Download className="h-4 w-4" /> PDF herunterladen
              </span>
            </a>
            <a
              href="/downloads/stundenzettel-vorlage.xlsx"
              download
              className="bg-white rounded-xl border shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col items-center text-center"
              data-testid="download-template-xlsx"
            >
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Stundenzettel als Excel</h3>
              <p className="text-sm text-gray-600 mb-4">Mit automatischer Summenberechnung pro Tag und Woche.</p>
              <span className="inline-flex items-center gap-2 text-emerald-600 font-medium text-sm">
                <Download className="h-4 w-4" /> Excel herunterladen
              </span>
            </a>
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Kein E-Mail-Eintrag nötig. Und wenn dir Excel irgendwann zu mühsam wird:{" "}
            <button onClick={() => navigate("/signup")} className="text-emerald-600 underline font-medium" data-testid="template-upgrade-link">
              ivoliq erfasst deine Zeiten automatisch
            </button>.
          </p>
        </div>
      </section>

      {/* ==================== GUIDE (LONG-FORM) ==================== */}
      <section id="guide" className="py-16 bg-white scroll-mt-28" data-testid="guide-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-10">
            Der große Zeiterfassung-Guide für Freelancer
          </h2>

          <div className="space-y-10">
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" /> Warum Zeiterfassung über deinen Gewinn entscheidet
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Studien und Umfragen unter Selbständigen zeigen ein wiederkehrendes Muster: Wer Zeiten nicht
                zeitnah erfasst, unterschätzt seinen Aufwand systematisch. Schon wer seine Stunden erst am
                Abend rekonstruiert, vergisst im Schnitt 10–15 % der geleisteten Arbeit – bei wöchentlicher
                Rekonstruktion sind es bis zu 25 %. Bei einem Stundensatz von 80 € und 120 fakturierbaren
                Stunden pro Monat bedeutet das bis zu 2.400 € Umsatzverlust – jeden Monat, unbemerkt.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Dazu kommt der zweite Effekt: Ohne Zeiterfassung kennst du deinen <strong>effektiven
                Stundensatz</strong> nicht. Ein Projekt mit 5.000 € Pauschale klingt gut – aber wenn du
                90 Stunden hineinsteckst, arbeitest du für 55 € pro Stunde, Korrekturschleifen und Meetings
                inklusive. Erst die lückenlose Erfassung macht sichtbar, welche Kunden und Projektarten
                sich wirklich lohnen. Genau darauf baust du dann deine Preisstrategie auf.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Layers className="h-5 w-5 text-emerald-600" /> Welche Methode passt zu dir? Stundenzettel, Excel oder Tool
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>1. Stundenzettel auf Papier:</strong> Der Klassiker – und für den Einstieg völlig
                okay. Lade dir oben unsere kostenlose PDF-Vorlage herunter. Nachteil: keine Auswertung,
                keine Summen, und am Monatsende musst du alles für die Rechnung abtippen.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>2. Excel-Vorlage:</strong> Ein Schritt weiter. Unsere Excel-Stundenzettel-Vorlage
                rechnet Tages- und Wochensummen automatisch. Für 1–2 Kunden funktioniert das gut. Ab drei
                parallelen Projekten wird die Pflege mühsam: keine laufende Stoppuhr, keine Projektauswertung,
                Fehleranfälligkeit durch manuelle Eingaben.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>3. Reines Zeiterfassungs-Tool (Toggl, Clockify & Co.):</strong> Komfortabel beim
                Erfassen – aber die Daten enden in einem Silo. Für die Rechnung exportierst du CSV-Dateien
                und tippst Positionen in ein zweites Tool. Genau an dieser Schnittstelle gehen Stunden verloren.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>4. Integrierte Lösung (Zeiterfassung + Rechnung):</strong> Der Ansatz von ivoliq.
                Timer, Kalenderansicht und Auswertung wie bei den Spezial-Tools – aber deine Zeiteinträge
                werden <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 underline">direkt zur Rechnung</Link>,
                inklusive Leistungsnachweis als Anhang. Kein Export, kein Abtippen, keine vergessene Stunde.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Scale className="h-5 w-5 text-emerald-600" /> Rechtslage: Musst du als Freelancer Zeiten erfassen?
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>Deutschland:</strong> Für Solo-Selbständige gibt es keine allgemeine Pflicht zur
                eigenen Zeiterfassung. Das BAG-Urteil von 2022 (1 ABR 22/21) und das EuGH-Urteil (C-55/18)
                verpflichten Arbeitgeber, die Arbeitszeit ihrer <em>Beschäftigten</em> zu erfassen. Relevant
                wirst du also erst, wenn du selbst Mitarbeiter einstellst. Ausnahme: In bestimmten Branchen
                (§ 17 MiLoG, z. B. Bau, Gebäudereinigung) gelten Aufzeichnungspflichten für geringfügig Beschäftigte.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>Österreich:</strong> Auch hier gilt das Arbeitszeitgesetz (AZG) nur für Arbeitnehmer.
                Für EPU ist Zeiterfassung freiwillig – aber als Nachweis bei Honorarstreitigkeiten und zur
                Abgrenzung von Scheinselbständigkeit äußerst wertvoll. Alle Details findest du in unserem{" "}
                <Link to="/zeiterfassung-freelancer-oesterreich" className="text-emerald-600 underline">Guide zur Zeiterfassung in Österreich</Link>.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Schweiz:</strong> Das Arbeitsgesetz (ArG) mit seiner Dokumentationspflicht gilt für
                Arbeitnehmende, nicht für Selbständigerwerbende. Wichtig ist die Zeiterfassung hier vor allem
                als Beleg gegenüber Auftraggebern und für die Abgrenzung der selbständigen Erwerbstätigkeit
                gegenüber der Ausgleichskasse (AHV).
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-emerald-600" /> Abrechenbare vs. nicht abrechenbare Stunden
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Die wichtigste Kennzahl deiner Selbständigkeit ist die <strong>fakturierbare Quote</strong>:
                der Anteil deiner Arbeitszeit, den du tatsächlich in Rechnung stellst. Bei den meisten
                Freelancern liegt sie zwischen 60 und 75 % – der Rest geht für Akquise, Angebote,
                Buchhaltung, E-Mails und Weiterbildung drauf.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Deshalb solltest du <em>beide</em> Kategorien erfassen: Nur wer weiß, dass von einer
                40-Stunden-Woche real 26 Stunden abrechenbar sind, kalkuliert seinen Stundensatz richtig.
                In ivoliq markierst du Einträge als abrechenbar oder intern und siehst deine Quote
                pro Woche, Monat und Projekt.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600" /> Zeiterfassung als Beweismittel: Betriebsprüfung & Honorarstreit
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Ein oft übersehener Aspekt: Deine Zeitaufzeichnungen sind Dokumente mit rechtlichem Gewicht.
                Bei einer <strong>Betriebsprüfung</strong> stützen sie die Plausibilität deiner Einnahmen –
                wenn du 120 Stunden im Monat fakturierst, aber laut Kalender im Urlaub warst, fragt das
                Finanzamt nach. Umgekehrt belegen saubere Aufzeichnungen, dass Reisekosten, Arbeitszimmer
                und Bewirtungen tatsächlich betrieblich veranlasst waren. Prüfer akzeptieren digitale
                Aufzeichnungen problemlos – wichtig ist, dass sie zeitnah geführt und nicht nachträglich
                konstruiert wurden.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Noch wichtiger wird die Dokumentation beim <strong>Honorarstreit</strong>. Zahlt ein Kunde
                nicht und behauptet, die abgerechneten Stunden seien überhöht, trägst du als Auftragnehmer
                die Beweislast für den Umfang deiner Leistung. Gerichte erkennen detaillierte
                Stundenaufstellungen mit Datum, Uhrzeit, Tätigkeitsbeschreibung und Projektbezug als
                starkes Indiz an – pauschale Angaben wie „Beratung, 40 Stunden" dagegen kaum. Die Praxis
                zeigt: Wer wöchentlich einen Leistungsnachweis mitschickt, bekommt selten überhaupt Streit,
                weil der Kunde den Aufwand laufend mitverfolgt und Einwände sofort statt nach drei Monaten kommen.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Ein dritter Punkt betrifft Freelancer, die überwiegend für einen einzigen Auftraggeber
                arbeiten: Die eigene, unabhängige Zeiterfassung mit freier Zeiteinteilung ist ein Baustein
                gegen den Verdacht der <strong>Scheinselbständigkeit</strong>. Wer dagegen im Zeitsystem
                des Kunden eingebucht wird, feste Anwesenheitszeiten dokumentiert und Urlaub „genehmigen"
                lässt, liefert der Prüfbehörde genau die Indizien, die ein abhängiges Beschäftigungsverhältnis
                nahelegen. Eigenes Tool, eigene Daten, eigene Hoheit – das ist die richtige Reihenfolge.
                Und falls es doch einmal zur Prüfung kommt, exportierst du deine vollständige
                Stundenhistorie mit wenigen Klicks als übersichtliche Aufstellung pro Kunde,
                Projekt und Zeitraum.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Check className="h-5 w-5 text-emerald-600" /> 7 Praxis-Tipps: So bleibst du bei der Zeiterfassung konsequent
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Das beste System scheitert an der Disziplin. Diese sieben Gewohnheiten haben sich bei
                Freelancern bewährt, die ihre Zeiterfassung über Jahre lückenlos führen:
              </p>
              <ol className="text-sm text-gray-600 leading-relaxed space-y-2 list-decimal pl-5 mb-3">
                <li><strong>Timer vor der Arbeit starten, nicht danach schätzen.</strong> Die Erfassung muss Teil des Arbeitsbeginns sein wie das Öffnen des Editors – nachträgliches Schätzen kostet nachweislich 10–25 % der Stunden.</li>
                <li><strong>Tätigkeiten sofort beschreiben.</strong> „Landingpage: Hero-Sektion umgesetzt" statt „Arbeit am Projekt" – aus guten Beschreibungen werden später automatisch aussagekräftige Rechnungspositionen.</li>
                <li><strong>Unterbrechungen ehrlich stoppen.</strong> Telefonate, Social Media, Mittagspause: Timer pausieren. Ehrliche Daten sind die Grundlage für ehrliche Kalkulation.</li>
                <li><strong>Auch Unbezahltes erfassen.</strong> Akquise, Buchhaltung, Weiterbildung – nur so kennst du deine fakturierbare Quote und deinen echten effektiven Stundensatz.</li>
                <li><strong>Freitags 10 Minuten Wochenreview.</strong> Lücken auffüllen, falsche Zuordnungen korrigieren, Wochenauswertung anschauen – solange die Erinnerung frisch ist.</li>
                <li><strong>Monatsende = Abrechnungstag.</strong> Fester Termin im Kalender: alle offenen Zeiteinträge in Rechnungen verwandeln. Mit ivoliq dauert das pro Kunde unter einer Minute.</li>
                <li><strong>Quartalsweise den effektiven Stundensatz prüfen.</strong> Umsatz pro Kunde geteilt durch alle erfassten Stunden (inkl. Meetings und Korrekturen) – Kunden unter deinem Zielsatz gehören auf den Prüfstand oder in die Preisverhandlung.</li>
              </ol>
              <p className="text-sm text-gray-600 leading-relaxed">
                Der gemeinsame Nenner: Reibung reduzieren. Jeder zusätzliche Klick, jedes zweite Tool und
                jeder Medienbruch senkt die Wahrscheinlichkeit, dass du konsequent bleibst. Genau deshalb
                gehören Zeiterfassung, Auswertung und Rechnungsstellung in eine einzige Oberfläche.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mt-3">
                Bonus-Technik für fokusintensive Arbeit: Kombiniere die Zeiterfassung mit festen
                Zeitblöcken. Wer morgens zwei ungestörte 90-Minuten-Blöcke für Kundenarbeit reserviert
                und Meetings, E-Mails und Admin auf den Nachmittag legt, erreicht erfahrungsgemäß eine
                deutlich höhere fakturierbare Quote – und die Timer-Daten zeigen dir schwarz auf weiß,
                ob dein Plan in der Realität hält oder von Unterbrechungen zerlegt wird.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" /> Auswertung: Diese 4 Kennzahlen solltest du aus deinen Zeiten ziehen
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Zeiterfassung ist kein Selbstzweck – ihr Wert entsteht in der Auswertung. Vier Kennzahlen
                genügen, um dein Geschäft wie ein Unternehmer zu steuern:
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>1. Fakturierbare Quote:</strong> Anteil der abrechenbaren an allen erfassten Stunden.
                Unter 55 % über mehrere Monate heißt: zu viel Verwaltung oder zu viel unbezahlte Extrameile.
                Über 85 % ist ebenfalls ein Warnsignal – dann fehlt Zeit für Akquise und Weiterbildung, und
                die nächste Auftragslücke ist programmiert.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>2. Effektiver Stundensatz pro Kunde:</strong> Gesamtumsatz eines Kunden geteilt durch
                <em> alle</em> für ihn geleisteten Stunden – inklusive Meetings, Abstimmungen und Korrekturschleifen.
                Diese Zahl weicht oft dramatisch vom vereinbarten Satz ab: 90 € auf dem Papier werden schnell
                zu 60 € effektiv, wenn ein Kunde dreimal so viele Abstimmungsrunden braucht wie andere.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>3. Aufwand pro Projekttyp:</strong> Wie viele Stunden kostet eine Landingpage, ein
                Logo-Paket, ein Beratungstag wirklich? Aus drei bis fünf abgeschlossenen Projekten desselben
                Typs entsteht eine belastbare Kalkulationsbasis für künftige Angebote – das Ende des
                Bauchgefühl-Pricings.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>4. Offene, noch nicht abgerechnete Stunden:</strong> Deine unsichtbare Forderung.
                Wer diesen Wert nicht kennt, verschenkt Liquidität – jede erfasste, aber nicht fakturierte
                Stunde ist ein zinsloser Kredit an deinen Kunden. In ivoliq siehst du diesen Betrag jederzeit
                und machst ihn mit einem Klick zur Rechnung.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-emerald-600" /> Der richtige Stundensatz zu deinen Stunden
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Erfasste Stunden sind nur so viel wert wie der Satz, mit dem du sie abrechnest. Die
                Faustregel „Angestellten-Stundenlohn mal 1,5 bis 2" ist ein Startpunkt – aber deine echten
                Zahlen (Sozialversicherung, Steuern, Betriebskosten, Urlaubstage, Auslastung) rechnest du
                besser konkret durch. Unser kostenloser Rechner macht das in 60 Sekunden, mit
                länderspezifischen Abgaben für Deutschland, Österreich und die Schweiz.
              </p>
              <Button variant="outline" onClick={() => navigate("/stundensatz-rechner")} data-testid="guide-calculator-cta">
                <Calculator className="h-4 w-4 mr-2" /> Zum Stundensatz-Rechner (DE/AT/CH)
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== DEMO CTA ==================== */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800" data-testid="demo-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Video className="h-4 w-4" /> Persönliche Demo
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Lass dir ivoliq in 30 Minuten zeigen
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Wir zeigen dir live, wie du mit ivoliq nie wieder Stunden verlierst – 
            von der Zeiterfassung bis zur fertigen Rechnung. Kostenlos und unverbindlich.
          </p>
          <Button
            size="lg"
            onClick={openCalModal}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            data-testid="demo-cta-button"
          >
            Kostenlose Demo buchen <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm text-gray-400">
            <span>30 Min. Videocall</span>
            <span>Keine Verpflichtung</span>
            <span>Individuelle Beratung</span>
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section id="faq" className="py-16 bg-white scroll-mt-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-8">
            Häufige Fragen zur Zeiterfassung
          </h2>
          <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="faq-list">
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== INTERNAL LINKS ==================== */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">Auch interessant für dich:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/zeiterfassung-freelancer-oesterreich" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung in Österreich</Link>
            <Link to="/zeiterfassung-freelancer#rechnung" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Zeiterfassung & Rechnungsstellung</Link>
            <Link to="/stundensatz-rechner" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Stundensatz-Rechner</Link>
            <Link to="/lexoffice-alternative" className="text-emerald-600 hover:text-emerald-700 underline text-sm">lexoffice Alternative</Link>
            <Link to="/sevdesk-alternative" className="text-emerald-600 hover:text-emerald-700 underline text-sm">sevDesk Alternative</Link>
            <Link to="/buchhaltung-freelancer-deutschland" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Buchhaltung Deutschland</Link>
            <Link to="/rechnungsvorlage-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Rechnungsvorlagen</Link>
            <Link to="/steuerruecklagen-freelancer" className="text-emerald-600 hover:text-emerald-700 underline text-sm">Steuerrücklagen Rechner</Link>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-16 bg-emerald-600" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Nie wieder Stunden verschenken
          </h2>
          <p className="text-emerald-100 mb-8">
            Starte jetzt kostenlos – Timer bis Rechnung in einer App.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/signup")}
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8"
              data-testid="final-cta-register"
            >
              Kostenlos starten <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={openCalModal}
              className="border-white text-white hover:bg-emerald-700 px-8"
              data-testid="final-cta-demo"
            >
              <Video className="h-5 w-5 mr-2" /> Demo buchen
            </Button>
          </div>
          <p className="text-sm text-emerald-200 mt-4">
            Keine Kreditkarte &middot; Free Plan für immer &middot; DSGVO-konform
          </p>
        </div>
      </section>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
